import { useEffect, useId, useState } from "react";
import type { TaskRow } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Button } from "../design/components/Button";
import { Icon } from "../design/components/Icon";
import { Reason } from "../design/components/Reason";
import { formatNumericTime } from "../i18n/format";
import { useT } from "../i18n/locale";
import { taskScopeOf, useOptionalAccess, useOptionalTab, type Access } from "../session/access";
import type { TabContext } from "../session/tab-context";
import { canCallFromTasks, useCallFromTasksBlock } from "./call-from-tasks";
import { taskSourceParam } from "./task-search";
import { dueNowQuery } from "./tasks-queries";
import "./due-now-banner.css";

// Il banner all'ora del richiamo concordato (piano M5, Task 20; D14; mock-up `mockup.js`, `renderAlerts`; guida della Smart List):
// in cima a ogni pagina del guscio, sopra la barra del Power Dialer, uno per ogni richiamo aperto, concordato con il Lead, assegnato
// a chi guarda e con l'ora arrivata (`GET /api/tasks/due-now`, il più vecchio prima; nella «Guarda come…» quelli della persona
// osservata, questione 320). Il guscio lo chiede ogni 60 secondi e a ogni ritorno sulla finestra; la fine di una chiamata e un
// richiamo fissato, spostato o fatto lo fanno rileggere, così il banner resta finché il richiamo non è completato e poi sparisce.
// Non c'è nel contesto di piattaforma né per chi non vede i Task.
//
// «Chiama adesso» apre la scheda del Lead e fa partire la chiamata come «Chiama» dei Task: dallo stato della cronologia, mai
// dall'indirizzo (ruling Q7 e Q57, `useCallFromTasks`), con `da` per «Torna ai Task» come nel mock-up. Chi non chiama da questa
// scheda (la «Guarda come…», senza `call`) ha «Apri», che apre soltanto la scheda (questioni 331 e 335). «Apri i Task» apre l'agenda.
// Finché la chiamata del browser è occupata, «Chiama adesso» è spento con il motivo di «Chiama» della scheda (M4).
//
// Disco e demo (piano M6, R7, Task 6): lo stesso banner porta i promemoria dai 30 minuti prima fino all'inizio, con «tra N min»,
// «Apri» della scheda e il link Meet quando Google l'ha dato; si annunciano a -30 e di nuovo a -5, e «Chiudi il promemoria» li
// nasconde fino al promemoria seguente. Non si chiamano dal banner.
//
// Gli screen reader (revisione del Task 20): i banner visibili sono gruppi semplici, non regioni vive, perché il loro «Sono le …»
// cambia ogni minuto; una sola regione nascosta e cortese dice una volta sola, con una frase che non cambia, ogni richiamo che entra.

export function DueNowBanner() {
  const access = useOptionalAccess();
  const tab = useOptionalTab();
  if (!access || !tab || access.isPlatform || taskScopeOf(access, "view") === "none") return null;
  return <DueNowBanners access={access} tab={tab} />;
}

/**
 * L'ora dell'orologio per «Sono le …»: da quando il server ha risposto (`since`), e poi a ogni cambio di minuto, così resta vera
 * anche se la risposta non cambia.
 */
function useMinuteClock(active: boolean, since: number): number {
  const [tick, setTick] = useState(() => Date.now());
  const now = Math.max(tick, since);
  useEffect(() => {
    if (!active) return;
    const timer = setTimeout(() => setTick(Date.now()), 60_000 - (now % 60_000));
    return () => clearTimeout(timer);
  }, [active, now]);
  return now;
}

/** Il promemoria di una disco o di una demo: -30 fino ai 5 minuti prima, poi -5 (R7). */
function reminderStage(row: TaskRow, now: number): 30 | 5 {
  return Date.parse(row.task.dueAt) - now <= 5 * 60_000 ? 5 : 30;
}

/** La chiave di una riga del banner: il richiamo, oppure l'appuntamento con il suo promemoria (così -5 si annuncia di nuovo). */
function bannerKey(row: TaskRow, now: number): string {
  return row.task.type === "callback" ? row.task.id : `${row.task.id}:${reminderStage(row, now)}`;
}

/** Quello che la regione viva dice: i richiami appena entrati nel banner; `serial` cambia a ogni nuovo arrivo. */
type Arrival = { rows: readonly TaskRow[]; serial: number };

/**
 * I richiami appena entrati nel banner (questione 335; revisione del Task 20): ciascuno si annuncia una volta, quando arriva. Un
 * richiamo che esce (completato, spostato) e poi torna si annuncia di nuovo; una lettura con le stesse righe non annuncia niente.
 * Senza banner la regione si svuota (in silenzio), così nessuno ci ritrova una frase vecchia.
 */
function useArrivals(rows: readonly TaskRow[], keyOf: (row: TaskRow) => string): Arrival | null {
  const [seen, setSeen] = useState<{ ids: readonly string[]; arrival: Arrival | null; serial: number }>({ ids: [], arrival: null, serial: 0 });
  const ids = rows.map(keyOf);
  if (ids.length !== seen.ids.length || ids.some((id, index) => id !== seen.ids[index])) {
    const arrived = rows.filter((row) => !seen.ids.includes(keyOf(row)));
    const serial = arrived.length > 0 ? seen.serial + 1 : seen.serial;
    setSeen({ ids, serial, arrival: arrived.length > 0 ? { rows: arrived, serial } : ids.length === 0 ? null : seen.arrival });
  }
  return seen.arrival;
}

function DueNowBanners({ access, tab }: { access: Access; tab: TabContext }) {
  const t = useT();
  const due = useQuery(dueNowQuery(tab));
  const all = due.data?.tasks ?? [];
  const now = useMinuteClock(all.length > 0, due.dataUpdatedAt);
  // I promemoria chiusi, per appuntamento e promemoria: tornano al promemoria seguente.
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(() => new Set());
  const rows = all.filter((row) => row.task.type === "callback" || (Date.parse(row.task.dueAt) > now && !dismissed.has(bannerKey(row, now))));
  const arrival = useArrivals(rows, (row) => bannerKey(row, now));
  const blocked = useCallFromTasksBlock();
  const timeZone = access.context.settings.timeZone;
  const callable = canCallFromTasks(access, tab);
  const clock = formatNumericTime(timeZone, new Date(now).toISOString());
  const announced = arrival?.rows
    .map((row) =>
      row.task.type === "callback"
        ? t("tasks.banner.announce", { lead: leadNameOf(row), time: formatNumericTime(timeZone, row.task.dueAt) })
        : t("appointments.banner.announce", { kind: t(`appointments.kind.${row.task.type}`), lead: leadNameOf(row), time: formatNumericTime(timeZone, row.task.dueAt) }),
    )
    .join(" ");
  return (
    <>
      {rows.length === 0 ? null : (
        <section className="g-due-now" aria-label={t("tasks.banner.label")}>
          {rows.map((row) =>
            row.task.type === "callback" ? (
              <DueNowItem key={row.task.id} row={row} clock={clock} timeZone={timeZone} callable={callable} blocked={blocked} />
            ) : (
              <AppointmentReminder
                key={bannerKey(row, now)}
                row={row}
                now={now}
                timeZone={timeZone}
                onDismiss={() => setDismissed((previous) => new Set(previous).add(bannerKey(row, now)))}
              />
            ),
          )}
        </section>
      )}
      {/* Una sola regione viva, nascosta, con una frase che non cambia: l'orologio dei banner, che cambia ogni minuto, ne resta fuori. */}
      <p className="g-visually-hidden" role="status">
        {arrival ? <span key={arrival.serial}>{announced}</span> : null}
      </p>
    </>
  );
}

/** Il nome del Lead nel banner: il nome, altrimenti il salone (che allora non si ripete nella seconda frase). */
function leadNameOf({ lead }: TaskRow): string {
  return lead.displayName ?? lead.salonName ?? "—";
}

type ItemProps = { row: TaskRow; clock: string; timeZone: string; callable: boolean; blocked: string | null };

function DueNowItem({ row, clock, timeZone, callable, blocked }: ItemProps) {
  const t = useT();
  const titleId = useId();
  const reasonId = useId();
  const { task, lead } = row;
  const time = formatNumericTime(timeZone, task.dueAt);
  const salon = lead.displayName === null ? null : lead.salonName;
  // La scheda aperta dal banner torna ai Task di oggi della persona del richiamo, come il mock-up (`da=task:oggi:<assegnatario>`).
  const link = { to: "/lead/$leadId", params: { leadId: lead.id }, search: { da: taskSourceParam({ chi: task.assignee.membershipId }) } } as const;
  let call;
  if (!callable) {
    call = (
      <Link {...link} className="g-button">
        {t("tasks.open")}
      </Link>
    );
  } else if (blocked !== null) {
    // Con la chiamata del browser occupata non si chiama: spento, con il motivo di «Chiama» della scheda (M4).
    call = (
      <Button variant="primary" disabled aria-describedby={reasonId}>
        <Icon name="phone" />
        {t("tasks.banner.call")}
      </Button>
    );
  } else {
    call = (
      <Link {...link} state={{ callFromTasks: task.id }} className="g-button" data-variant="primary">
        <Icon name="phone" />
        {t("tasks.banner.call")}
      </Link>
    );
  }
  return (
    <div className="g-due-now__item" role="group" aria-labelledby={titleId}>
      <Icon name="clock" />
      <p className="g-due-now__text">
        <strong id={titleId}>{t("tasks.banner.title", { time: clock, lead: leadNameOf(row) })}</strong>
        <span aria-hidden="true"> · </span>
        <span>{salon ? t("tasks.banner.text", { salon, time }) : t("tasks.banner.textNoSalon", { time })}</span>
      </p>
      <div className="g-due-now__actions">
        {call}
        <Link to="/task" className="g-button">
          {t("tasks.banner.open")}
        </Link>
      </div>
      {callable && blocked !== null ? (
        <Reason tone="warning" id={reasonId} className="g-due-now__reason">
          {blocked}
        </Reason>
      ) : null}
    </div>
  );
}

/** Il promemoria di una disco o di una demo (R7, Task 6): quando, con chi, fra quanti minuti; la scheda e il Meet, se c'è. */
function AppointmentReminder({ row, now, timeZone, onDismiss }: { row: TaskRow; now: number; timeZone: string; onDismiss: () => void }) {
  const t = useT();
  const titleId = useId();
  const { task, lead } = row;
  const minutes = Math.max(1, Math.ceil((Date.parse(task.dueAt) - now) / 60_000));
  const meetUrl = task.appointment?.meetUrl ?? null;
  return (
    <div className="g-due-now__item" role="group" aria-labelledby={titleId}>
      <Icon name="calendar" />
      <p className="g-due-now__text">
        <strong id={titleId}>
          {t("appointments.banner.title", { kind: t(`appointments.kind.${task.type === "demo" ? "demo" : "disco"}`), time: formatNumericTime(timeZone, task.dueAt), lead: leadNameOf(row) })}
        </strong>
        <span aria-hidden="true"> · </span>
        <span>{t("appointments.banner.soon", { minutes })}</span>
      </p>
      <div className="g-due-now__actions">
        {meetUrl ? (
          <a className="g-button" data-variant="primary" href={meetUrl} target="_blank" rel="noopener noreferrer">
            {t("appointments.banner.meet")}
          </a>
        ) : null}
        <Link to="/lead/$leadId" params={{ leadId: lead.id }} search={{ da: taskSourceParam({ chi: task.assignee.membershipId }) }} className="g-button">
          {t("tasks.open")}
        </Link>
        <Button onClick={onDismiss}>{t("appointments.banner.dismiss")}</Button>
      </div>
    </div>
  );
}
